import * as React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { DashboardController } from "../../../controllers";
import ERoute from "../../../ERoute";
import UserWorkspaceSettingHelper from "../../../helpers/UserWorkspaceSettingHelper";
import { FinancialYear, UserWorkspaceSettingScope } from "../../../types";
import ButtonPanel from "../../Button/ButtonPanel";
import PageLoader from "../../Page/PageLoader";
import Panel from "../../Panel/Panel";
import YearlyRevenueGoal from "../YearlyRevenue/YearlyRevenueGoal";

interface YearlyRevenueGoalData {
  collected_revenue: string;
  correction_revenue: string;
  expected_revenue: string;
  expected_revenue_metadata: {
    unbilled_time_entries: number;
    outstanding_invoices: number;
    projects: number;
    recurring_invoices: number;
  };
  revenue_goal: string;
  financial_year: FinancialYear;
}

interface IProps {
  currency: string;
  numberFormat: string;
}

const YearlyRevenueGoalWidget = (props: IProps) => {
  const { currency, numberFormat } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<YearlyRevenueGoalData | null>(null);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await DashboardController.getYearlyRevenueGoal();
      setData(response);
    } catch (error) {
      console.error("Failed to fetch yearly revenue goal:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !data) {
    return (
      <Panel className="h-full flex flex-col mb-0!" contentClasses="min-h-0 flex-1 overflow-auto" title={t("General::Revenue Goal")}>
        <PageLoader />
      </Panel>
    );
  }

  const {
    collected_revenue,
    correction_revenue,
    expected_revenue,
    expected_revenue_metadata,
    revenue_goal,
    financial_year,
  } = data;

  return (
    <Panel
      className="h-full flex flex-col mb-0!"
      contentClasses="min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
      title={t("General::Revenue Goal")}
      headerRight={
        UserWorkspaceSettingHelper.hasScope(UserWorkspaceSettingScope.SETTING) ? (
          <ButtonPanel
            icon="pen"
            text={t("Dashboard::Edit revenue goal")}
            onClick={() => {
              history.push(ERoute.PATH_SETTINGS_GENERAL);
            }}
          />
        ) : null
      }
    >
      <YearlyRevenueGoal
        financialYear={financial_year}
        collectedRevenue={Number(collected_revenue)}
        correctionRevenue={Number(correction_revenue)}
        expectedRevenue={Number(expected_revenue)}
        expectedRevenueMetadata={expected_revenue_metadata}
        revenueGoal={Number(revenue_goal)}
        currency={currency}
        numberFormat={numberFormat}
      />
    </Panel>
  );
};

export default YearlyRevenueGoalWidget;
